import {
  assertDialogHost,
  closeAllDialogs,
  closeFunctionalDialog,
  openFunctionalDialog,
  requestConfirm,
  type ConfirmDialogOptions,
  type FunctionalDialogOptions,
} from './dialogService';

/**
 * 两个入口 hook。**二选一，一眼可见**：
 *
 *   `useConfirm()`  按钮确认：锚在触发按钮上，从按钮里飞出来，点外部/Esc = 取消。
 *   `useDialog()`   功能性弹窗：居中 + 模糊背景，即时出现，不表演。
 *
 * 它们不是"两种模态"，而是两个视觉档位：选错了在调用点就能看出来
 * （传了 `anchor` 的一定是前者，传了 `content` 的一定是后者）。
 *
 * 为什么是 hook 而不是导出两个函数：`useX` 这个名字在调用点自带一句"这是在组件里用的"，
 * 并且能在**渲染期**就报出"没挂宿主"这个配置错误（见下）。
 */

/** 确认函数。取消（含 Esc、点击外部）返回 `false`，绝不抛错、绝不静默吞掉 */
export type ConfirmFn = (options: ConfirmDialogOptions) => Promise<boolean>;

/**
 * 功能性弹窗的句柄。
 * `open()` 会返回它，因为"用里面的按钮把自己关掉"是最常见的需求，
 * 而调用方通常不在内容组件里（内容是一段创建时就固定下来的 JSX）。
 */
export interface DialogHandle {
  readonly id: string;
  /** 关闭这一个弹窗（立即，无动画）。重复调用是空操作 */
  close(): void;
}

export interface DialogApi {
  /** 打开功能性弹窗 */
  open(options: FunctionalDialogOptions): DialogHandle;
  /** 关闭所有弹窗（链路断开、切换机体这类事件用）。确认类一律以 `false` 落定 */
  closeAll(): void;
}

/**
 * 按钮确认。
 *
 * 用法：
 * ```tsx
 * const confirm = useConfirm();
 * const ok = await confirm({
 *   title: '立即返航？',
 *   consequence: '飞机将立即停止当前任务并飞回起飞点。',
 *   confirmLabel: '立即返航',
 *   danger: 'critical',
 *   confirmPhrase: '返航',
 *   anchor: event.currentTarget,   // 决定弹出位置与飞入起点
 * });
 * ```
 * 不用 `window.confirm`：它会阻塞渲染线程（地面站里等于冻结遥测），且样式与文案无法统一。
 */
export function useConfirm(): ConfirmFn {
  // 渲染期就报错：静默失败的表现是"点了按钮没反应"，那比一条明确的中文报错难查得多
  assertDialogHost();
  // requestConfirm 是模块级函数，引用恒稳 —— 不需要 useCallback 包一层
  return requestConfirm;
}

/** 功能性弹窗（居中 + 模糊背景，无动画） */
export function useDialog(): DialogApi {
  assertDialogHost();
  return DIALOG_API;
}

/** 模块级常量：身份稳定，可以安全地进依赖数组 */
const DIALOG_API: DialogApi = {
  open: (options) => {
    const id = openFunctionalDialog(options);
    return {
      id,
      close: () => closeFunctionalDialog(id),
    };
  },
  closeAll: closeAllDialogs,
};
